import { useState, useEffect } from 'react';
import { TrendingUp, Activity, Users, DollarSign } from 'lucide-react';
import { adminApi } from '../services/adminApi';

export default function Analytics() {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [usageData, setUsageData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      const data = await adminApi.getUsageAnalytics(period);
      setUsageData(data);
    } catch (error) {
      console.error('[ANALYTICS] Failed to load analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-normal tracking-tight text-gray-900 mb-1">
            Analytics
          </h1>
          <p className="text-sm text-gray-600 font-normal tracking-tight">
            Platform usage and performance metrics
          </p>
        </div>
        
        {/* Period Selector */}
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as any)}
          className="admin-input w-auto"
        >
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="admin-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-600 font-normal tracking-tight mb-1">
                API Requests
              </p>
              <p className="text-2xl font-normal tracking-tight text-gray-900">
                {usageData?.api_requests?.toLocaleString() || '0'}
              </p>
            </div>
            <Activity className="w-5 h-5 text-gray-600" />
          </div>
        </div>

        <div className="admin-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-600 font-normal tracking-tight mb-1">
                AI Tokens Used
              </p>
              <p className="text-2xl font-normal tracking-tight text-gray-900">
                {usageData?.ai_tokens?.toLocaleString() || '0'}
              </p>
            </div>
            <TrendingUp className="w-5 h-5 text-gray-600" />
          </div>
        </div>

        <div className="admin-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-600 font-normal tracking-tight mb-1">
                Active Users
              </p>
              <p className="text-2xl font-normal tracking-tight text-gray-900">
                {usageData?.active_users || '0'}
              </p>
            </div>
            <Users className="w-5 h-5 text-gray-600" />
          </div>
        </div>

        <div className="admin-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-600 font-normal tracking-tight mb-1">
                Revenue ({period})
              </p>
              <p className="text-2xl font-normal tracking-tight text-gray-900">
                ${usageData?.revenue?.toLocaleString() || '0'}
              </p>
            </div>
            <DollarSign className="w-5 h-5 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Charts Placeholder */}
      <div className="admin-card">
        <h2 className="text-lg font-normal tracking-tight text-gray-900 mb-6">
          Usage Over Time
        </h2>
        {isLoading ? (
          <p className="text-sm text-gray-600">Loading analytics...</p>
        ) : (
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <p className="text-sm text-gray-600">Chart visualization coming soon</p>
          </div>
        )}
      </div>

      {/* Top AI Employees */}
      <div className="admin-card">
        <h2 className="text-lg font-normal tracking-tight text-gray-900 mb-6">
          Most Used AI Employees
        </h2>
        <div className="space-y-3">
          {isLoading ? (
            <p className="text-sm text-gray-600">Loading data...</p>
          ) : (
            <>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm font-normal tracking-tight">Financial Analyst</span>
                <span className="text-sm text-gray-600">12,450 requests</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm font-normal tracking-tight">Document Processor</span>
                <span className="text-sm text-gray-600">8,320 requests</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-normal tracking-tight">Investment Analyst</span>
                <span className="text-sm text-gray-600">6,780 requests</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
